import { inject, Injectable } from '@angular/core';
import { createLogger } from '@hunting-nature/shared-core/logging';
import { Store } from '@ngrx/store';
import { defer, of, type Observable } from 'rxjs';
import { delay, map, switchMap, tap } from 'rxjs/operators';

import { CannesBleRegistrationService } from './cannes-ble-registration.service';
import { DeviceApiService, RATE_LIMIT_BACKOFF_MS } from './device-api.service';
import { RegistrationError } from './device-registration-error';

import type { DeviceChallengeResponse } from '@hunting-nature/shared-core/interfaces/device-registration.interface';

import type { HuntingProduct } from '../../pages/hunting-products/redux/hunting-products.reducer';
import { DeviceRegistrationActions } from '../../pages/hunting-products/redux/device-registration.actions';

/**
 * Service for the business logic of the device registration flow.
 *
 * Encapsulates:
 * - BLE proof collection (write challenge → delay → read response → base64 encode)
 * - Registration completion (validate product/hardwareId → call API)
 * - Rate limit backoff management
 *
 * Effects dispatch this service's observables and map results to NgRx actions.
 */
@Injectable({
  providedIn: 'root',
})
export class DeviceRegistrationFlowService {
  private static readonly DEVICE_SIGNING_DELAY_MS = 1_500;

  private readonly deviceApiService = inject(DeviceApiService);
  private readonly cannesBleRegistrationService = inject(CannesBleRegistrationService);
  private readonly store = inject(Store);
  private readonly log = createLogger('DeviceRegistrationFlowService');

  private rateLimitTimeoutId?: ReturnType<typeof setTimeout>;

  /**
   * Collects the device's signing proof via BLE.
   *
   * Flow:
   * 1. Write the backend challenge to the device
   * 2. Wait DEVICE_SIGNING_DELAY_MS (1500ms) for the device to compute its counter-signature
   * 3. Read the 512-byte device signature
   * 4. Convert Uint8Array → binary string → base64
   *
   * @returns `{ challengeId, deviceSignature }` ready for submission to the backend
   */
  public collectDeviceProof$(deviceId: string, challenge: DeviceChallengeResponse): Observable<{ challengeId: string; deviceSignature: string }> {
    const writeChallenge$ = this.cannesBleRegistrationService.writeDeviceSigningChallenge$(deviceId, challenge).pipe(
      tap(() => {
        this.log.debug('Challenge written to device successfully');
      })
    );

    const readResponse$ = this.cannesBleRegistrationService.readDeviceSigningResponse$(deviceId).pipe(
      map((deviceSignatureBytes: Uint8Array) => {
        let binaryString = '';
        for (let index = 0; index < deviceSignatureBytes.length; index++) {
          binaryString += String.fromCharCode(deviceSignatureBytes[index]);
        }
        const base64Signature = btoa(binaryString);

        this.log.debug('Device proof collected successfully', {
          deviceId,
          challengeId: challenge.challengeId,
          deviceSignatureLength: base64Signature.length,
        });

        return { challengeId: challenge.challengeId, deviceSignature: base64Signature };
      })
    );

    return writeChallenge$.pipe(
      delay(DeviceRegistrationFlowService.DEVICE_SIGNING_DELAY_MS),
      switchMap(() => readResponse$)
    );
  }

  /**
   * Completes device registration after validating the product and hardwareId.
   *
   * @throws {RegistrationError} if the product is not found or hardwareId cannot be derived
   * @throws {Error} on API failure (mapped from HTTP response via DeviceApiService)
   */
  public completeRegistrationWithValidation$(
    deviceId: string,
    pairedProducts: Array<HuntingProduct>,
    hardwareId: string | undefined,
    challengeId: string,
    deviceSignature: string
  ): Observable<{ hardwareId: string }> {
    const product = pairedProducts.find(p => p.deviceId === deviceId);
    if (!product) {
      throw new Error(RegistrationError.NETWORK_ERROR);
    }

    if (!hardwareId) {
      throw new Error(RegistrationError.NETWORK_ERROR);
    }

    return this.deviceApiService
      .completeAssignment$(hardwareId, {
        challengeId,
        deviceSignature,
      })
      .pipe(
        map(result => {
          this.log.debug('Registration succeeded', { hardwareId: result.hardwareId });
          return { hardwareId: result.hardwareId };
        })
      );
  }

  /**
   * Starts a rate-limit backoff timer.
   *
   * Clears any existing timer first, then schedules a new one that dispatches `rateLimitExpired`.
   *
   * @returns `{ until: backoffUntil }` for the effect to dispatch as `rateLimitBackoffSet`
   */
  public startRateLimitBackoff$(retryAfterMs?: number): Observable<{ until: number }> {
    return defer(() => {
      if (this.rateLimitTimeoutId) clearTimeout(this.rateLimitTimeoutId);

      const durationMs = retryAfterMs ?? RATE_LIMIT_BACKOFF_MS;
      const backoffUntil = Date.now() + durationMs;
      this.log.debug('Rate limited - backing off', { backoffUntil: new Date(backoffUntil).toISOString() });

      this.rateLimitTimeoutId = setTimeout(() => {
        this.rateLimitTimeoutId = undefined;
        this.store.dispatch(DeviceRegistrationActions.rateLimitExpired());
      }, durationMs);

      return of({ until: backoffUntil });
    });
  }

  /**
   * Clears any pending rate-limit backoff timer. Called when a new registration
   * attempt is initiated.
   */
  public clearRateLimitBackoff(): void {
    if (this.rateLimitTimeoutId) {
      clearTimeout(this.rateLimitTimeoutId);
      this.rateLimitTimeoutId = undefined;
    }
  }
}
